/**
 * Returns a new Date object set to the start of the day (00:00:00) for the given input.
 *
 * @param {Date|string} inputDate - The date to normalize (can be a Date object or date string).
 * @returns {Date} - A new Date object with time set to 00:00:00.
 */
export const normaliseToStartOfDay = (inputDate) => {
  const date = new Date(inputDate);
  date.setHours(0, 0, 0, 0);
  return date;
};

/**
 * Checks if the target date is the same or before the reference date (ignoring time).
 *
 * @param {Date} referenceDate - The date to compare against.
 * @param {Date|string} targetDate - The date to check (can be a Date object or date string).
 * @returns {boolean} - True if targetDate is the same or before referenceDate (at day level), false otherwise.
 */
export const dateIsSameOrBefore = (referenceDate, targetDate) => {
  const target = new Date(targetDate);

  const normalisedTarget = normaliseToStartOfDay(target);
  const normalisedReference = normaliseToStartOfDay(referenceDate);

  return normalisedTarget.getTime() <= normalisedReference.getTime();
};

/**
 * Checks if the target date is the same or after the reference date (ignoring time).
 *
 * @param {Date} referenceDate - The date to compare against.
 * @param {Date|string} targetDate - The date to check (can be a Date object or date string).
 * @returns {boolean} - True if targetDate is the same or after referenceDate (at day level), false otherwise.
 */
export const dateIsSameOrAfter = (referenceDate, targetDate) => {
  const target = new Date(targetDate);

  const normalisedTarget = normaliseToStartOfDay(target);
  const normalisedReference = normaliseToStartOfDay(referenceDate);

  return normalisedTarget.getTime() >= normalisedReference.getTime();
};

/**
 * Formats a given date to "Month Year" format (e.g., "November 2025").
 *
 * @param {Date|string} dateValue - The date to format (can be a Date object or date string).
 * @returns {string} - The formatted date in "Month Year" format.
 */
export const formatToMonthYear = (dateValue) => {
  const date = new Date(dateValue);

  const options = { year: 'numeric', month: 'long' }; // e.g., "November 2025"
  return date.toLocaleDateString(undefined, options); // undefined uses the user's locale
};

/**
 * Calculates the number of whole days between today and a target date.
 *
 * The function normalizes both today's date and the target date to midnight
 * to ensure time-of-day differences don't affect the result.
 *
 * @param {string|Date} dateString - The target date, e.g. "2025-12-11T00:00:00"
 *                                   or a Date object.
 * @returns {number} The day difference:
 *                   - Positive = target date is in the future
 *                   - Zero     = today
 *                   - Negative = target date is in the past
 */
export const daysUntil = (dateString) => {
  const target = new Date(dateString);
  const now = new Date();

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfTarget = new Date(target.getFullYear(), target.getMonth(), target.getDate());

  const diffMs = startOfTarget - startOfToday;
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  return diffDays;
};

/**
 * Ensures the year portion of a YYYY-MM-DD date string is limited to 4 digits.
 *
 * @param {string} dateStr - The date string to sanitise.
 * @returns {string} - The sanitized date string with a max 4-digit year.
 */
export const limitYearToFourDigits = (dateStr) => {
  if (!dateStr || dateStr.length < 4) return dateStr;

  const parts = dateStr.split('-');

  if (parts[0] && parts[0].length > 4) {
    parts[0] = parts[0].slice(0, 4);
    return parts.join('-');
  }

  return dateStr;
};
